import {
    ArticlePublication,
    DataLayerEventName,
    EventPublication,
    LiveBlogEvents,
    SharingEvent,
    wait,
} from '@news-mono/web-common'
import {
    EventPostV4DTO,
    KilledEventPostV4DTO,
} from '@west-australian-newspapers/publication-types'
import { WebsocketServiceMessageWithPayload } from '@west-australian-newspapers/websocket-message-types'
import {
    default as React,
    FC,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react'
import { Logger } from 'typescript-log'
import { useWebsocketServiceClient } from '../../websocket-service-client/use-websocket-service'
import {
    JumpToTopButton,
    LiveEventLoadButton,
    LiveEventTimeline,
} from './Components'
import { LiveEventMilestones } from './Components/LiveEventMilestones'
import {
    dedupeEventPosts,
    getPostIdFromUrl,
    isDebuggingEvents,
    isPublishedMilestone,
    liveEventDebug,
    scrollToElement,
    useDebugPanel,
    useFetchEventPosts,
    useIsComponentOnScreen,
    usePostManager,
} from './helpers'
import {
    StyledButtonContainer,
    StyledContainer,
    StyledJumpButtonContainer,
    StyledReportingLiveBubble,
    StyledReportingLiveContainer,
    StyledReportingLiveDivider,
    StyledReportingLiveText,
} from './LiveEvent.styled'

export interface LiveEventProps {
    publication: EventPublication | ArticlePublication
    log: Logger
    adUnitPath: string
    onEvent: (event: LiveBlogEvents | SharingEvent) => void
    onWebsocketEvent: (
        event: WebsocketServiceMessageWithPayload<
            EventPostV4DTO | KilledEventPostV4DTO
        >,
    ) => void
}

export const LiveEvent: FC<LiveEventProps> = ({
    publication,
    log,
    adUnitPath,
    onEvent,
    onWebsocketEvent,
}) => {
    const { fetchEventPosts, isFetching } = useFetchEventPosts()

    useWebsocketServiceClient<EventPostV4DTO | KilledEventPostV4DTO>({
        entityId: publication.id,
        onSubscribe: (messages) =>
            messages.map(({ payload }) => payload).map((post) => addPost(post)),
        onMessage: (message) => {
            if (message.payload) {
                addDebugEvent('Websocket message received %o', message.payload)
                addPost(message.payload)
                // Check for a milestone and update
                if (isPublishedMilestone(message.payload)) {
                    setMilestones(
                        dedupeEventPosts([message.payload], milestones),
                    )
                }
            }
            // Update article updated timestamp in header
            onWebsocketEvent(message)
        },
    })

    const postsPerPage = 10

    const {
        activePosts,
        knownPosts,
        setActivePosts,
        addPosts,
        addPost,
        latestKnownPost,
        oldestVisiblePost,
        morePosts,
    } = usePostManager(
        publication.kind === 'event' ? publication.entries.documents : [],
    )

    interface FetchPostsArgs {
        postId?: string
        before?: number
        after?: number
        callback?: (posts: EventPostV4DTO[]) => Promise<void>
        replaceActivePosts?: boolean
    }

    const fetchPosts = useCallback(
        async ({
            postId,
            before = 0,
            after = 0,
            callback,
            replaceActivePosts,
        }: FetchPostsArgs) => {
            const response = await fetchEventPosts({
                publicationId: publication.id,
                reference: postId,
                after,
                before,
            })

            // Minimise turbulent loading
            replaceActivePosts && setActivePosts([])

            await wait(5)

            addPosts(response.documents, postId ?? response.documents[0].id, {
                postsAbove: response.totalAfter,
                postsBelow: response.totalBefore,
            })

            callback && (await callback(response.documents))
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [publication.id, addPosts],
    )

    /**
     * Initial state for article publications
     */

    // posts are prefetched for event publications
    const [initialPostsFetched, setInitialPostsFetched] = useState(
        publication.kind === 'event',
    )

    useEffect(() => {
        if (publication.kind === 'article') {
            addDebugEvent('Fetching initial posts for article publication')
            fetchPosts({
                before: 10,
                after: 0,
                callback: async (posts) => {
                    onWebsocketEvent({
                        kind: 'create-post',
                        entityId: publication.id,
                        payload: posts[0],
                    })
                    setInitialPostsFetched(true)
                    addDebugEvent(
                        'Initial posts fetched for article publication',
                    )
                },
            })
        }
    }, [publication.kind])

    /**
     * Deep linking
     */

    const deepLinkPostId = getPostIdFromUrl()

    const isDeepLinkPostVisible = !!activePosts.find(
        ({ id }) => id === deepLinkPostId,
    )

    const [deepLinkPostsFetching, setDeepLinkPostsFetching] = useState(false)

    useEffect(() => {
        if (deepLinkPostsFetching) {
            return
        }

        if (!initialPostsFetched) {
            addDebugEvent(
                'Initial posts have not been fetched, skipping deep link check',
            )
            return
        }

        if (!deepLinkPostId) {
            addDebugEvent('No deep link post in hash')
            return
        }

        if (isDeepLinkPostVisible && !morePosts.after) {
            addDebugEvent('Deep link post found in active posts, scrolling')
            scrollToElement(deepLinkPostId)
            return
        }

        addDebugEvent('Deep link post not found in active posts, fetching')

        scrollToElement(jumpContainer.current)

        setDeepLinkPostsFetching(true)
        fetchPosts({
            postId: deepLinkPostId,
            after: 0,
            before: 9,
            replaceActivePosts: true,
            callback: async () => {
                setDeepLinkPostsFetching(false)
                await wait(5)

                scrollToElement(deepLinkPostId)
            },
        })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deepLinkPostId, isDeepLinkPostVisible, initialPostsFetched])

    /**
     * Lazy Load
     */

    const [lazyLoadTriggered, setLazyLoadTriggered] = useState(false)

    const handleLazyLoad = async () => {
        if (!lazyLoadTriggered && !isFetching && oldestVisiblePost) {
            addDebugEvent('Lazy loading next posts')
            setLazyLoadTriggered(true)
            await fetchPosts({
                postId: oldestVisiblePost.id,
                before: postsPerPage,
            })
        }
    }

    /**
     * Load More
     */

    const handleLoadMore = async () => {
        addDebugEvent('Load more clicked')
        if (oldestVisiblePost) {
            await fetchPosts({
                postId: oldestVisiblePost.id,
                before: postsPerPage,
            })

            onEvent({
                type: DataLayerEventName.loadMoreOlderPosts,
                originator: `LiveEvent`,
                payload: {
                    publicationId: publication.id,
                    lastPostPrior: oldestVisiblePost.id,
                },
            })
        }
    }

    /**
     * Jump to top
     */

    const jumpContainer = useRef(null)

    const handleJumpToTop = async () => {
        liveEventDebug('Jumping to top')
        scrollToElement(jumpContainer.current)

        if (morePosts.after) {
            addDebugEvent(
                'More posts detected, deep linking to latest known post',
            )
            window.location.hash = latestKnownPost.id
        }
    }

    const isActiveEvent = publication.classification?.label === 'live'

    const { isVisible, componentRef: containerRef } = useIsComponentOnScreen()

    /**
     * Milestones
     */

    const [milestones, setMilestones] = useState(
        publication.kind === 'event' ? publication.entries.milestones : [],
    )

    const hasMilestones = milestones.length > 0

    /**
     * Debug
     */

    const debugObj = {
        activePosts: activePosts.length,
        knownPosts: knownPosts.length,
        morePosts,
        isFetching,
        isVisible,
        latestKnownPost: latestKnownPost?.id,
        oldestVisiblePost: oldestVisiblePost?.id,
        deepLinkPostId,
        isDeepLinkPostVisible,
        lazyLoadTriggered,
    }

    const { DebugPanel, addDebugEvent } = useDebugPanel({ debugObj })

    return (
        <StyledContainer ref={containerRef}>
            <StyledJumpButtonContainer ref={jumpContainer}>
                <JumpToTopButton
                    onClick={handleJumpToTop}
                    inViewport={isVisible}
                    isLoading={isFetching}
                    newPostCount={morePosts.after}
                />
            </StyledJumpButtonContainer>
            {hasMilestones && (
                <LiveEventMilestones
                    milestones={milestones}
                    onEvent={onEvent}
                />
            )}
            <StyledReportingLiveContainer isActive={isActiveEvent}>
                {isActiveEvent && (
                    <>
                        <StyledReportingLiveBubble />
                        <StyledReportingLiveText>
                            Reporting
                            <span> LIVE</span>
                        </StyledReportingLiveText>
                    </>
                )}
            </StyledReportingLiveContainer>
            {isActiveEvent && <StyledReportingLiveDivider />}
            <LiveEventTimeline
                posts={activePosts}
                eventTitle={publication.heading}
                log={log}
                adUnitPath={adUnitPath}
                triggerLazyLoad={handleLazyLoad}
                onEvent={onEvent}
            />
            <StyledButtonContainer>
                {morePosts.before > 0 && (
                    <LiveEventLoadButton
                        isLoading={isFetching}
                        onClick={handleLoadMore}
                    />
                )}
            </StyledButtonContainer>
            {isDebuggingEvents && <DebugPanel />}
        </StyledContainer>
    )
}
